import { Profile } from "../data/Profile";

import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { useFirebase } from "./FirebaseProvider";
import { FirebaseApp } from "firebase/app";
import { doc, getDoc, setDoc } from "firebase/firestore";

const PROFILE_COLLECTION = 'profiles';

const DEFAULT_PROFILE = {
    name: 'Unnamed User',
    institution: 'Unnaffiliated',
    isAdmin: false
} as Profile;

export type ProfileContextType = {
    /**
     * The user's profile.
     */
    profile: Profile | null;

    /**
     * Gets the profile for the given user ID.
     * @param userId - The user ID.
     * @returns The profile for the given user ID.
     */
    getProfileForUserId: (userId: string) => Promise<Profile>;
}

export const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const [profile, setProfile] = useState<Profile | null>(null);
    const { user, db } = useFirebase();

    const getProfileForUserId = useCallback(async (userId: string) => {
        const ref = doc(db, PROFILE_COLLECTION, userId);
        const docSnap = await getDoc(ref);

        if (docSnap.exists()) {
            return docSnap.data() as Profile;
        } else {
            // Create a new profile for the user
            await setDoc(ref, DEFAULT_PROFILE);

            return DEFAULT_PROFILE;
        }
    }, [db]);

    useEffect(() => {
        if (user) {
            getProfileForUserId(user.uid)
                .then(profile => {
                    setProfile(profile);
                });
        } else {
            setProfile(null);
        }
    }, [user, setProfile, getProfileForUserId]);

    return (
        <ProfileContext.Provider value={{ profile, getProfileForUserId }}>
            {children}
        </ProfileContext.Provider>
    )
}

export const useProfile = () => {
    const context = useContext(ProfileContext);

    if (!context) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }

    return context;
}